import PropTypes from 'prop-types';
import { useObservable, useObservableCallback, useObservableState } from 'observable-hooks';
import {
    combineLatest,
    connect,
    distinctUntilChanged,
    map,
    Observable,
    pairwise,
    ReplaySubject,
    scan,
    startWith,
    switchMap,
    withLatestFrom,
    zip,
} from 'rxjs';
import {
    isFunction,
    isMatch,
    matches,
    isObject,
    isEqual,
    isArray,
    difference,
    intersection,
} from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { fromReactHookFormControl } from '../../reactForm/utils';
import { NOTIFICATION_VARIANT, useNotifications } from '../../notifications/Notifications';
import { confirm, ConfirmType } from '../../confirm/Confirm';
import { applyNamingConvention, namingConventions$ } from '../../../utils/naming-conventions';
import { extractSecondsFromLeqInterval } from '../../../utils/leq-interval-converter';
import { Markdown } from '../../../utils/markdown';
import { shareReplayRefCount } from '../../../utils/rxjs';

function calculateMidpointOfLeqInterval(values) {
    const lxEqInterval = extractSecondsFromLeqInterval(values.lxEqInterval);
    return lxEqInterval / 2;
}

// Wrapping this in a function to make the translation evaluate on element rendering.
function createWarnings() {
    const isSbrA2017Foundation = matches({
        guideLine: 'SBR_A_2017',
        buildingLevel: 'foundation',
    });

    const isAtopEnabled = matches({ atopEnabled: true });

    const atopWarningFields = ['guideLine', 'buildingLevel', 'atopEnabled'];

    // The intent of these warnings is to inform users of configuration issues that may lead
    // to undesirable behavior or results within the system and, where applicable, to propose
    // fixes or guide the user in making adjustments.
    const warnings = [
        {
            criteria: { noiseSavingEnabled: true },
            fix: { noiseSavingEnabled: false },
            message: gettext('NOISE_SAVE_WARNING'),
        },
        {
            criteria: { vtopEnabled: false },
            fix: { vtopEnabled: true },
            message: gettext('VTOP_DISABLE_WARNING'),
        },
        {
            criteria(values) {
                return isSbrA2017Foundation(values) && !isAtopEnabled(values);
            },
            responsibleFields: atopWarningFields,
            fix: { atopEnabled: true },
            autoFix: true,
            message: gettext('ATOP_DISABLE_WARNING'),
        },
        {
            criteria(values) {
                return !isSbrA2017Foundation(values) && isAtopEnabled(values);
            },
            responsibleFields: atopWarningFields,
            fix: { atopEnabled: false },
            autoFix: true,
            message: gettext('ATOP_ENABLE_WARNING'),
        },
        {
            criteria: { guideLine: 'SN640312a', vectorEnabled: false },
            fix: { vectorEnabled: true },
            autoFix: true,
            message: gettext('VECTOR_DISABLE_WARNING'),
        },
        // Enhanced sound clip coverage warning:
        //  - This specific warning was added to ensure that a sound clip can fully capture
        //    the Leq interval whenever a configured limit is exceeded.
        //  - It warns users if the pre-trigger or post-trigger time settings do not adequately
        //    cover the midpoint of the Leq interval, which is crucial for correctly anchoring
        //    the sound clip.
        //  - This warning has multiple paths for resolution, supporting flexible user intervention
        //    by allowing adjustment of either interval or trigger settings.
        {
            criteria(values) {
                if (values.soundClipAlarmSource !== 'leq') {
                    return false;
                }

                // Warning should not trigger when the user hits backspace in the input fields.
                if (values.tracePreTrigger == null || values.tracePostTrigger == null) {
                    return false;
                }

                const midpoint = calculateMidpointOfLeqInterval(values);

                // Check if the midpoint of the Leq interval exceeds either the pre-trigger or
                // post-trigger time.
                // This condition helps determine if the currently configured trigger times are
                // inadequate to fully cover the central portion of the Leq interval, indicating
                // a potential loss of sound clip coverage at the beginning or end of the interval.
                return midpoint > values.tracePreTrigger || midpoint > values.tracePostTrigger;
            },
            responsibleFields: [
                'soundClipAlarmSource',
                'lxEqInterval',
                'tracePreTrigger',
                'tracePostTrigger',
            ],
            // This warning has multiple solutions; therefore, a hardcoded fix might not
            // satisfy the user. We will not offer a fix for this.
            fix: null,
            message(values, formSchema) {
                // Get the leq field to extract the option translation later on.
                const field = formSchema.fields.getByName('lxEqInterval');
                const midpoint = calculateMidpointOfLeqInterval(values);
                const preSecondsLost = Math.max(0, midpoint - values.tracePreTrigger);
                const postSecondsLost = Math.max(0, midpoint - values.tracePostTrigger);

                return interpolate(
                    gettext('SOUND_CLIP_DOES_NOT_COVER_LEQ_INTERVAL_WARNING'),
                    {
                        lxEqInterval: field.options.get(values.lxEqInterval),
                        preTrigger: values.tracePreTrigger,
                        postTrigger: values.tracePostTrigger,
                        preSecondsLost,
                        postSecondsLost,
                    },
                    true
                );
            },
        },
        // Excessive battery and data usage warning:
        {
            criteria: { lxEqInterval: 'interval1Second' },
            fix: { lxEqInterval: 'interval5Seconds' },
            message: gettext('INTERVAL_1_SECOND_WARNING'),
        },
    ];

    return warnings.map((warning) => {
        if (isFunction(warning.criteria) && !isArray(warning.responsibleFields)) {
            throw new Error(
                'When the warning `criteria` is a function, you also needs to specify ' +
                    '`responsibleFields` in order to make the code understand which dirty ' +
                    'form values are responsible for triggering the warning.'
            );
        }

        return {
            ...warning,
            responsibleFields: warning.responsibleFields ?? Object.keys(warning.criteria),
        };
    });
}

function matchWarningCriteria(criteria, values) {
    if (isFunction(criteria)) {
        return criteria(values);
    }

    if (isObject(criteria)) {
        return isMatch(values, criteria);
    }

    throw new Error(`Don't know how to match criteria of type ${typeof criteria}`);
}

function trackChangedFields() {
    return connect(
        (sharedSource$) => {
            // This stream calculates the changedKeys for each emission from sharedSource$.
            const changedKeys$ = sharedSource$.pipe(
                startWith(null),
                pairwise(),
                map(([previousValues, currentValues]) => {
                    // Find keys that have changed.
                    const changedKeys = Object.entries(previousValues ?? {}).reduce(
                        (keys, [key, value]) => {
                            if (!isEqual(value, currentValues[key])) {
                                keys.push(key);
                            }
                            return keys;
                        },
                        []
                    );

                    return changedKeys;
                })
            );

            return zip(sharedSource$, changedKeys$);
        },
        { connector: () => new ReplaySubject(1) }
    );
}

function isWarningTriggeredByDirectChange(warning, changedFields) {
    const matchingFields = intersection(
        warning.fix ? Object.keys(warning.fix) : warning.responsibleFields,
        changedFields
    );
    return matchingFields.length > 0;
}

function isWarningTriggeredByIndirectChange(warning, changedFields) {
    const noDirectChangeFields = difference(warning.responsibleFields, Object.keys(warning.fix));
    const matchingFields = intersection(noDirectChangeFields, changedFields);
    return matchingFields.length > 0;
}

export function ConfigurationWarning({ formSchema, serverValuesUpdate$ }) {
    const { setValue, formControl } = useFormContext();
    const { addNotification } = useNotifications();

    const warnings = useMemo(() => createWarnings(), []);

    const applyFix = useCallback(
        (warning) => {
            Object.entries(warning.fix).forEach(([fieldName, value]) => {
                setValue(fieldName, value);
            });
        },
        [setValue]
    );

    // Keep track of warning criteria that were shown in an alert popup but got dismissed.
    const [dismissWarning, dismissedWarnings$] = useObservableCallback((warning$) =>
        warning$.pipe(
            scan((state, warning) => {
                state.push(warning.criteria);
                return state;
            }, []),
            startWith([]),
            shareReplayRefCount()
        )
    );

    const values$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap(([fc]) => fromReactHookFormControl(fc, 'values')),
                trackChangedFields()
            ),
        [formControl]
    );

    const triggeredWarnings$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap((inputs) => combineLatest(inputs)),
                withLatestFrom(serverValuesUpdate$),
                map(
                    ([
                        [[values, changedFields], dismissedWarnings, namingConventions],
                        serverValuesUpdate,
                    ]) => {
                        // Determine the triggered warnings by matching the warning criteria
                        // against the form values and render the warning messages.
                        const triggeredWarnings = warnings
                            .filter((warning) => matchWarningCriteria(warning.criteria, values))
                            .map((warning) => ({
                                ...warning,
                                message: applyNamingConvention(
                                    isFunction(warning.message)
                                        ? warning.message(values, formSchema)
                                        : warning.message,
                                    namingConventions
                                ),
                            }));

                        // Create an extra list of warnings that are triggered by the current
                        // user's form changes.
                        const triggeredWarningsByDirtyResponsibleFields = triggeredWarnings
                            // Ignore warnings triggered by values coming from the server.
                            .filter(() => !serverValuesUpdate)
                            // Ignore warnings that are already dismissed.
                            .filter((warning) => !dismissedWarnings.includes(warning.criteria))
                            // Only use warnings that are triggered by dirty fields.
                            .filter((warning) =>
                                isWarningTriggeredByDirectChange(warning, changedFields)
                            );

                        // A warning is considered auto-fixable if it is marked with `autoFix: true`
                        // and the triggering change was not on the field that requires fixing.
                        // For example, consider the atopWarning. The responsible fields for the
                        // warning condition are `['guideLine', 'buildingLevel', 'atopEnabled']`.
                        // The field that needs fixing is `atopEnabled`. If a change was made to
                        // `['guideLine', 'buildingLevel']`, we can safely auto-fix the issue. If
                        // the user directly changes `atopEnabled`, we don't want to auto-fix as
                        // it would essentially undo the user's UI action. In that case, we just
                        // want to show the warning popup.
                        const autoFixableWarnings = triggeredWarnings.filter((warning) => {
                            // Ignore warnings triggered by values coming from the server.
                            if (serverValuesUpdate) {
                                return false;
                            }

                            if (!warning.autoFix) {
                                return false;
                            }

                            return (
                                !isWarningTriggeredByDirectChange(warning, changedFields) &&
                                isWarningTriggeredByIndirectChange(warning, changedFields)
                            );
                        });

                        autoFixableWarnings.forEach((warning) => {
                            applyFix(warning);
                        });

                        return [
                            triggeredWarnings.filter(
                                (warning) => !autoFixableWarnings.includes(warning)
                            ),
                            triggeredWarningsByDirtyResponsibleFields.filter(
                                (warning) => !autoFixableWarnings.includes(warning)
                            ),
                        ];
                    }
                ),
                // Emit only distinct values for efficiency.
                distinctUntilChanged(isEqual)
            ),
        [values$, dismissedWarnings$, namingConventions$]
    );

    const [triggeredWarnings, triggeredWarningsByDirtyResponsibleFields] = useObservableState(
        triggeredWarnings$,
        [[], []]
    );

    // Effect for displaying configuration warnings at the top of the form. These warnings
    // are triggered by the currently loaded form values, not just by changes made by the user.
    useEffect(() => {
        const notifications = triggeredWarnings.map((warning) =>
            addNotification({
                message: (
                    <>
                        <Markdown>{warning.message}</Markdown>
                        {warning.fix && (
                            <button className="pl-1 underline" onClick={() => applyFix(warning)}>
                                {gettext('FIX_WARNING')}
                            </button>
                        )}
                    </>
                ),
                variant: NOTIFICATION_VARIANT.WARNING,
            })
        );

        return () => {
            notifications.forEach((notification) => {
                notification.remove();
            });
        };
    }, [triggeredWarnings, addNotification, applyFix]);

    // Effect for displaying configuration warning alerts when the user selects a warning
    // configuration.
    useEffect(() => {
        const confirmCloseFunctions = triggeredWarningsByDirtyResponsibleFields.map((warning) =>
            confirm(
                warning.fix
                    ? // If the warning is auto fixable, show the confirm modal, which
                      // offers a confirm and cancel button.
                      {
                          message: warning.message,
                          convertMarkdown: true,
                          onCancel: () => applyFix(warning),
                          onConfirm: () => dismissWarning(warning),
                      }
                    : // If the warning is not fixable, show the info modal, which only
                      // shows an ok button.
                      {
                          message: warning.message,
                          convertMarkdown: true,
                          afterClose: () => {
                              dismissWarning(warning);
                          },
                          type: ConfirmType.INFO,
                      }
            )
        );

        return () => {
            confirmCloseFunctions.forEach((closeFunction) => closeFunction());
        };
    }, [triggeredWarningsByDirtyResponsibleFields, applyFix, dismissWarning]);

    return null;
}
ConfigurationWarning.propTypes = {
    formSchema: PropTypes.object.isRequired,
    serverValuesUpdate$: PropTypes.instanceOf(Observable),
};
